// ---- Common furniture ----
// Generic, theme-neutral pieces usable by ANY building (cottage, castle, shop):
// rugs, benches, stools, shelves, lamps, storage, tables, hearths, a plain bed.
// Castle/heraldic-specific pieces (throne, banquet table, four-poster, tapestry,
// crest) live in heraldic.ts. Both build from meshkit prims via the ctx, authored
// feet-at-y=0 in the building's LOCAL coords; the structure's build() places them.
//
// Conventions:
//   - SOLID pieces wrap visuals in keep() (never faded by cutaway) + emit a
//     collider so you bump into them.
//   - `y0` lifts a solid piece onto an upper floor (added to every y + collider).
//   - Variation is derived deterministically from indices (never Math.random).

import * as THREE from "three";
import { shade } from "../../color.ts";
import {
  type Ctx, WOOD, WOOD_LT, WOOD_DK, GOLD, IRON, CUSHION, STONE_DAIS,
  RUG_FIELD, RUG_BORDER, RUG_MOTIF, CANDLE, FLAME, EMBER, SOOT, LINEN, BOOK_COLS,
} from "./shared.ts";

// ---- Rug ---------------------------------------------------------------------
// A long runner: blue border, red field, a gold inner stripe, and a row of gold
// lozenges down the centre. Laid in razor-thin stacked slabs so the layers never
// z-fight the floor or trip the player. No collider.
export function rug(c: Ctx, cx: number, cz: number, w: number, d: number) {
  const { box, keep } = c;
  keep(() => {
    box(cx, 0.03, cz, w, 0.06, d, RUG_BORDER);             // border
    box(cx, 0.05, cz, w - 0.7, 0.06, d - 0.7, RUG_FIELD);  // field
    box(cx, 0.07, cz, w - 1.5, 0.06, d - 1.5, RUG_MOTIF);  // inner gold stripe
    box(cx, 0.09, cz, w - 1.9, 0.06, d - 1.9, RUG_FIELD);  // field inside the stripe
    const n = Math.max(1, Math.floor(d / 2.6));
    for (let i = 0; i < n; i++) {
      const z = cz - d / 2 + (i + 0.5) * (d / n);
      box(cx, 0.11, z, 0.7, 0.06, 0.7, RUG_MOTIF);
      box(cx, 0.13, z, 0.34, 0.06, 0.34, RUG_FIELD);
    }
  });
}

// ---- Bench -------------------------------------------------------------------
// A simple oak bench running along z. `face` = which way sitters look (ry
// radians; default +x — pass -π/2 for a bench whose table is on its -x side).
export function bench(c: Ctx, cx: number, cz: number, length: number, face = Math.PI / 2) {
  const { box, keep, collideBox } = c;
  keep(() => {
    box(cx, 0.55, cz, 0.5, 0.12, length, WOOD);
    for (const sz of [-1, 1]) {
      box(cx, 0.27, cz + sz * (length / 2 - 0.25), 0.46, 0.54, 0.22, WOOD_DK);
    }
  });
  (c.standBox ?? collideBox)(cx - 0.3, cx + 0.3, cz - length / 2, cz + length / 2, 0, 0.6); // top standable (gravity plan)
  // Two sitters along the run (seat surface = slab top, 0.55 + 0.06).
  for (const sz of [-1, 1]) c.seat?.(cx, 0.61, cz + sz * (length / 4), face);
}

// ---- Stool -------------------------------------------------------------------
// A small round oak stool — a single seat. `y0` lifts it onto an upper floor;
// `face` = the sitter's facing (round stool, so it's a pure authoring choice).
export function stool(c: Ctx, cx: number, cz: number, y0 = 0, face = 0) {
  const { cylinder, keep, collideCircle } = c;
  keep(() => {
    cylinder(cx, y0, cz, 0.22, 0.26, 0.52, WOOD);       // leg post
    cylinder(cx, y0 + 0.52, cz, 0.32, 0.32, 0.1, WOOD_LT); // seat top
  });
  (c.standCircle ?? collideCircle)(cx, cz, 0.34, y0, y0 + 0.62);
  c.seat?.(cx, y0 + 0.62, cz, face);
}

// ---- Bookshelf ---------------------------------------------------------------
// A tall oak case standing against a wall, its open face toward +x (faceX=1) or
// -x (faceX=-1). `y0` lifts the whole case onto an upper floor.
export function bookshelf(c: Ctx, cx: number, cz: number, faceX: 1 | -1, y0 = 0) {
  const { box, keep, collideBox } = c;
  const W = 2.6, H = 3.6, D = 0.8; // width along z, height, depth along x
  keep(() => {
    box(cx - faceX * (D / 2 - 0.06), y0 + H / 2, cz, 0.12, H, W, WOOD_DK);  // back panel
    box(cx, y0 + H / 2, cz - W / 2, D, H, 0.14, WOOD);                       // side
    box(cx, y0 + H / 2, cz + W / 2, D, H, 0.14, WOOD);                       // side
    box(cx, y0 + H - 0.08, cz, D, 0.16, W, WOOD);                            // top
    box(cx, y0 + 0.08, cz, D, 0.16, W, WOOD);                               // base
    const shelves = 4;
    for (let s = 0; s <= shelves; s++) {
      const y = y0 + 0.2 + s * ((H - 0.4) / shelves);
      box(cx, y, cz, D - 0.1, 0.08, W - 0.18, WOOD_LT);                 // shelf board
      if (s === shelves) continue;
      const books = 9;
      for (let i = 0; i < books; i++) {
        const z = cz - W / 2 + 0.18 + (i + 0.5) * ((W - 0.36) / books);
        const bh = 0.62 + ((i * 7 + s * 3) % 4) * 0.07;
        const col = BOOK_COLS[(i + s) % BOOK_COLS.length];
        box(cx + faceX * 0.04, y + 0.04 + bh / 2, z, D - 0.26, bh, (W - 0.36) / books - 0.05, col);
      }
    }
  });
  (c.standBox ?? collideBox)(cx - D / 2, cx + D / 2, cz - W / 2, cz + W / 2, y0, y0 + H);
}

// ---- Chandelier --------------------------------------------------------------
// An iron ring of candles hung by a chain from the ceiling, with a warm light at
// its centre. `ceilingY` is the underside of the floor slab above.
export function chandelier(c: Ctx, cx: number, cz: number, ceilingY: number) {
  const { box, cylinder, cone, keep, light } = c;
  const ringY = ceilingY - 1.3;
  keep(() => {
    cylinder(cx, ceilingY - 1.0, cz, 0.05, 0.05, 1.0, IRON); // chain
    cylinder(cx, ringY, cz, 0.18, 0.18, 0.14, IRON);         // central hub
    const R = 0.95, N = 8;
    for (let i = 0; i < N; i++) {
      const a = (i / N) * Math.PI * 2;
      const bx = cx + Math.cos(a) * R, bz = cz + Math.sin(a) * R;
      box((cx + bx) / 2, ringY, (cz + bz) / 2, 0.06, 0.06, 0.06, IRON);
      box(bx, ringY, bz, 0.24, 0.14, 0.24, IRON);
      cylinder(bx, ringY + 0.07, bz, 0.07, 0.07, 0.34, CANDLE);
      cone(bx, ringY + 0.41, bz, 0.08, 0.2, FLAME);
    }
  });
  light(cx, ringY, cz);
}

// ---- Barrel ------------------------------------------------------------------
// A bulging oak barrel bound by iron hoops.
export function barrel(c: Ctx, cx: number, cz: number) {
  const { cylinder, keep, collideCircle } = c;
  keep(() => {
    cylinder(cx, 0, cz, 0.38, 0.34, 0.35, WOOD);     // bottom (narrow)
    cylinder(cx, 0.34, cz, 0.43, 0.43, 0.45, WOOD);  // belly (wide)
    cylinder(cx, 0.79, cz, 0.34, 0.4, 0.35, WOOD);   // top (narrow)
    cylinder(cx, 0.24, cz, 0.44, 0.44, 0.07, IRON);  // lower hoop
    cylinder(cx, 0.78, cz, 0.41, 0.41, 0.07, IRON);  // upper hoop
    cylinder(cx, 1.1, cz, 0.33, 0.33, 0.06, WOOD_DK); // lid
  });
  (c.standCircle ?? collideCircle)(cx, cz, 0.46, 0, 1.2);
}

// ---- Crate -------------------------------------------------------------------
// A planked wooden crate with lighter rim framing. `s` is its edge length.
export function crate(c: Ctx, cx: number, cz: number, s: number) {
  const { box, keep, collideBox } = c;
  keep(() => {
    box(cx, s / 2, cz, s, s, s, WOOD_DK);
    box(cx, s - 0.04, cz, s * 1.02, 0.12, s * 1.02, WOOD_LT); // top rim
    box(cx, 0.06, cz, s * 1.02, 0.12, s * 1.02, WOOD_LT);     // bottom rim
    box(cx, s / 2, cz, s * 1.01, s * 0.16, s * 0.16, WOOD);   // mid band (x faces)
    box(cx, s / 2, cz, s * 0.16, s * 0.16, s * 1.01, WOOD);   // mid band (z faces)
  });
  (c.standBox ?? collideBox)(cx - s / 2, cx + s / 2, cz - s / 2, cz + s / 2, 0, s);
}

// ---- Storage chest -----------------------------------------------------------
// A banded oak chest with a stepped lid and a gold lock plate. `y0` lifts it onto
// an upper floor.
export function chest(c: Ctx, cx: number, cz: number, y0 = 0) {
  const { box, keep, collideBox } = c;
  const W = 1.5, D = 0.9, bodyH = 0.72;
  keep(() => {
    box(cx, y0 + bodyH / 2, cz, W, bodyH, D, WOOD);                    // body
    box(cx, y0 + bodyH + 0.13, cz, W * 1.03, 0.26, D * 1.03, WOOD_DK); // lid
    for (const sx of [-0.32, 0.32])
      box(cx + sx * W, y0 + bodyH / 2 + 0.06, cz, 0.12, bodyH + 0.34, D * 1.05, IRON);
    box(cx, y0 + bodyH - 0.02, cz + D / 2, 0.24, 0.28, 0.07, GOLD);    // lock plate
  });
  (c.standBox ?? collideBox)(cx - W / 2, cx + W / 2, cz - D / 2, cz + D / 2, y0, y0 + bodyH + 0.3);
}

// ---- Round table -------------------------------------------------------------
// A circular oak pedestal table set with a lit candle and an open book, ringed by
// four stools. `y0` lifts it onto an upper floor.
export function roundTable(c: Ctx, cx: number, cz: number, y0 = 0) {
  const { box, cylinder, cone, keep, collideCircle } = c;
  const R = 1.35, topY = 1.0;
  keep(() => {
    cylinder(cx, y0 + topY, cz, R, R, 0.14, WOOD_LT);     // top
    cylinder(cx, y0, cz, 0.32, 0.42, topY, WOOD);         // pedestal
    cylinder(cx, y0, cz, 0.7, 0.7, 0.12, WOOD_DK);        // foot
    cylinder(cx - 0.3, y0 + topY + 0.07, cz, 0.07, 0.07, 0.32, CANDLE);
    cone(cx - 0.3, y0 + topY + 0.39, cz, 0.07, 0.16, FLAME);
    box(cx + 0.45, y0 + topY + 0.07, cz, 0.8, 0.06, 0.6, BOOK_COLS[1]); // open book
    box(cx + 0.45, y0 + topY + 0.1, cz, 0.06, 0.04, 0.56, LINEN);       // pages
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const sx = cx + Math.cos(a) * 1.95, sz = cz + Math.sin(a) * 1.95;
      cylinder(sx, y0, sz, 0.32, 0.3, 0.56, WOOD);        // stool seat post
      cylinder(sx, y0 + 0.56, sz, 0.36, 0.36, 0.1, WOOD_LT); // seat top
    }
  });
  (c.standCircle ?? collideCircle)(cx, cz, R + 0.1, y0, y0 + topY);
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const sx = cx + Math.cos(a) * 1.95, sz = cz + Math.sin(a) * 1.95;
    (c.standCircle ?? collideCircle)(sx, sz, 0.38, y0, y0 + 0.66);
    // Each ring stool seats one, facing the table centre.
    c.seat?.(sx, y0 + 0.66, sz, Math.atan2(cx - sx, cz - sz));
  }
}

// ---- Bed ---------------------------------------------------------------------
// A plain framed bed (no canopy): oak frame, pale mattress, a folded blanket over
// the foot, a pillow at the head (toward -z), and a low headboard. `y0` lifts it
// onto an upper floor. One footprint collider. For the grand canopied version see
// `fourPosterBed` in heraldic.ts.
export function bed(c: Ctx, cx: number, cz: number, y0 = 0) {
  const { box, keep, collideBox } = c;
  const W = 2.0, L = 3.4, mattY = 0.72;
  keep(() => {
    box(cx, y0 + 0.34, cz, W, 0.46, L, WOOD);                                 // frame
    box(cx, y0 + mattY, cz, W - 0.24, 0.36, L - 0.24, LINEN);                 // mattress
    box(cx, y0 + mattY + 0.13, cz + L * 0.18, W - 0.24, 0.16, L * 0.46, CUSHION); // blanket over the foot
    box(cx, y0 + mattY + 0.18, cz - L / 2 + 0.5, W - 0.7, 0.22, 0.66, LINEN); // pillow
    box(cx, y0 + 1.05, cz - L / 2 + 0.1, W, 1.2, 0.2, WOOD_DK);               // headboard
  });
  (c.standBox ?? collideBox)(cx - W / 2, cx + W / 2, cz - L / 2, cz + L / 2, y0, y0 + 1.0);
}

// ---- Fireplace ---------------------------------------------------------------
// A stone hearth built against a wall, with a mantel shelf, a sooty firebox,
// glowing embers and a warm light. (x,z) is the wall's inner face; (nx,nz) its
// outward normal — the hearth projects INTO the room. `y0` puts it on an upper floor.
export function fireplace(
  c: Ctx, x: number, z: number,
  nx: 0 | 1 | -1, nz: 0 | 1 | -1,
  y0 = 0,
) {
  const { box, keep, collideBox, light } = c;
  const ix = -nx, iz = -nz;          // inward (into the room)
  const ax = nz !== 0;               // wall runs along X when its normal is along Z
  const DEPTH = 0.72;
  const place = (u: number, y: number, d: number, wU: number, hy: number, dN: number, col: number) => {
    const px = ax ? x + u : x + ix * d;
    const pz = ax ? z + iz * d : z + u;
    box(px, y0 + y, pz, ax ? wU : dN, hy, ax ? dN : wU, col);
  };
  const STONE_H = shade(STONE_DAIS, 0.04), STONE_D = shade(STONE_DAIS, -0.12);
  keep(() => {
    place(0, 1.7, 0.1, 3.0, 3.4, 0.22, STONE_DAIS);       // chimney breast against the wall
    place(-1.2, 1.3, DEPTH / 2, 0.6, 2.6, DEPTH, STONE_H); // left jamb
    place(1.2, 1.3, DEPTH / 2, 0.6, 2.6, DEPTH, STONE_H);  // right jamb
    place(0, 2.55, DEPTH / 2, 3.0, 0.55, DEPTH, STONE_D);  // lintel over the opening
    place(0, 2.98, DEPTH / 2 + 0.12, 3.5, 0.24, DEPTH + 0.34, STONE_H); // mantel shelf
    place(0, 0.12, DEPTH / 2, 2.6, 0.24, DEPTH, STONE_D);  // raised hearth slab
    place(0, 1.05, 0.16, 1.8, 1.9, 0.08, SOOT);            // firebox shadow
    place(0, 0.42, 0.36, 1.2, 0.26, 0.34, WOOD_DK);
    place(0, 0.42, 0.34, 0.9, 0.34, 0.3, EMBER);
    place(0, 0.34, 0.32, 1.3, 0.2, 0.42, FLAME);
  });
  const fp = DEPTH + 0.34;
  if (ax) collideBox(x - 1.5, x + 1.5, Math.min(z, z + iz * fp), Math.max(z, z + iz * fp), y0, y0 + 1.0);
  else collideBox(Math.min(x, x + ix * fp), Math.max(x, x + ix * fp), z - 1.5, z + 1.5, y0, y0 + 1.0);
  light(x + ix * 0.55, y0 + 1.2, z + iz * 0.55);
}

// ---- Shop counter ------------------------------------------------------------
// Merchant counter: solid oak desk with a raised top, front panel, and a couple
// of display goods. Shopkeeper stands on the back side (toward +x when length
// runs along z). `length` is the run along z.
export function shopCounter(c: Ctx, cx: number, cz: number, length = 4.2) {
  const { box, cylinder, keep, collideBox } = c;
  const w = 1.15; // depth along x
  const topY = 1.05;
  keep(() => {
    box(cx, topY / 2, cz, w, topY, length, WOOD);                 // body
    box(cx, topY + 0.06, cz, w + 0.12, 0.12, length + 0.12, WOOD_LT); // top
    box(cx - w / 2 + 0.06, topY / 2, cz, 0.08, topY - 0.08, length - 0.16, WOOD_DK); // front face band
    // display goods on the counter
    box(cx - 0.15, topY + 0.28, cz - length * 0.22, 0.45, 0.32, 0.45, WOOD_DK); // crate
    cylinder(cx + 0.15, topY + 0.12, cz + length * 0.18, 0.22, 0.24, 0.42, WOOD); // jar
    cylinder(cx + 0.15, topY + 0.54, cz + length * 0.18, 0.18, 0.18, 0.06, 0xb9bcc4);
    box(cx - 0.05, topY + 0.2, cz + length * 0.32, 0.5, 0.08, 0.35, 0x6a4a2a); // folded cloth
  });
  (c.standBox ?? collideBox)(cx - w / 2, cx + w / 2, cz - length / 2, cz + length / 2, 0, topY + 0.12);
}

// ---- Shop sign ("General Store") ---------------------------------------------
// Simple wooden plaque centered on the door. Real `fillText` on a CanvasTexture
// when `document` exists (client / studio); server world-build is DOM-free so it
// gets the board only (collision/visual bulk still fine). (cx,cz) on the door
// wall; (nx,nz) = outward normal (default west −x).

/** Bake a sign label (1 or 2 lines). Returns null headlessly (no document). */
function makeShopSignTexture(line1: string, line2?: string): THREE.Texture | null {
  if (typeof document === "undefined") return null;
  const w = 512, h = 160;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  // Transparent bg so wood shows around letters (alphaTest on the material)
  ctx.clearRect(0, 0, w, h);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Slight stroke so thin glyphs stay readable on oak
  const draw = (text: string, y: number, size: number) => {
    ctx.font = `bold ${size}px Georgia, "Times New Roman", serif`;
    ctx.lineWidth = 4;
    ctx.strokeStyle = "rgba(42,24,16,0.35)";
    ctx.strokeText(text, w / 2, y);
    ctx.fillStyle = "#1a1008";
    ctx.fillText(text, w / 2, y);
  };
  if (line2) {
    draw(line1, h * 0.34, 50);
    draw(line2, h * 0.7, 46);
  } else {
    draw(line1, h / 2, 54);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

export function shopSign(
  c: Ctx, cx: number, cz: number,
  nx: 0 | 1 | -1 = -1, nz: 0 | 1 | -1 = 0,
  line1 = "General",
  line2 = "Store",
) {
  const { box, mesh, keep } = c;
  // Wall-run axis (u): for west face (nx=-1) this is +z.
  const ux = nz, uz = -nx;
  const boardW = 2.6;
  const boardH = 0.72;
  const boardT = 0.14;
  // Raised above the ~3u door lintel so it sits cleanly over the opening.
  const boardY = 3.85;
  // (nx,nz) = OUTWARD wall normal. Outside = center + outward * t.
  const out = (t: number) => ({ x: cx + nx * t, z: cz + nz * t });

  const sizeN = (t: number, along: number) =>
    [Math.abs(nx) * t + Math.abs(nz) * along, Math.abs(nz) * t + Math.abs(nx) * along] as const;

  keep(() => {
    // Pegs under the board
    for (const s of [-1, 1] as const) {
      const u = s * (boardW * 0.32);
      const p = out(0.06);
      const [sx, sz] = sizeN(0.16, 0.1);
      box(p.x + ux * u, boardY - boardH / 2 - 0.05, p.z + uz * u, sx, 0.08, sz, WOOD_DK);
    }

    // Wood board
    {
      const p = out(boardT / 2 + 0.1);
      const [sx, sz] = sizeN(boardT, boardW);
      box(p.x, boardY, p.z, sx, boardH, sz, WOOD);
    }
    // Planed face
    {
      const p = out(boardT + 0.12);
      const [sx, sz] = sizeN(0.04, boardW - 0.1);
      box(p.x, boardY, p.z, sx, boardH - 0.1, sz, WOOD_LT);
    }

    // Real fillText on a transparent plane (client / studio). Server has no
    // document — board still builds; letters appear once the client runs.
    // line1/line2 come from furnish args (strings); empty line2 → single line.
    const label = makeShopSignTexture(line1, line2 || undefined);
    if (label) {
      const p = out(boardT + 0.15);
      const pw = boardW - 0.28;
      const ph = boardH - 0.2;
      const geo = new THREE.PlaneGeometry(pw, ph);
      // Default plane faces +Z; rotate so +Z points outward (nx, 0, nz).
      const yaw = Math.atan2(nx, nz); // nx=-1,nz=0 → −π/2
      geo.applyMatrix4(new THREE.Matrix4().makeRotationY(yaw));
      geo.translate(p.x, boardY, p.z);
      mesh(geo, 0xffffff, { map: label, transparent: true });
    }
  });
}
